// Supplies page content to every section. The public site uses the content
// baked into the build; the admin editor supplies the draft plus an `edit` API,
// which switches the same components into edit mode.
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { defaultContent, SiteData } from './index';
import { safeImagePath } from '../urls';

export interface FieldSpec {
  name: string;
  label: string;
  type?: 'text' | 'textarea' | 'date' | 'url' | 'email' | 'image' | 'select';
  /** Choices for a `select` field. */
  options?: { value: string; label: string }[];
  hint?: string;
  placeholder?: string;
  required?: boolean;
  /** Returns an error message, or undefined if the value is fine. */
  validate?: (value: string) => string | undefined;
}

export interface FormSpec {
  title: string;
  fields: FieldSpec[];
  values: Record<string, string | undefined>;
  submitLabel?: string;
  /** Shows a Delete button; the result is then `{ deleted: true }`. */
  deleteLabel?: string;
}

export type FormResult = { values: Record<string, string> } | { deleted: true } | undefined;

export interface EditApi {
  /**
   * Replaces one section of the draft. Rapid updates sharing a `coalesce` key
   * (e.g. dragging a color picker) count as one step for Undo.
   */
  update<K extends keyof SiteData>(key: K, value: SiteData[K], options?: { coalesce?: string }): void;
  /** Lets the admin pick one photo; resolves to its site path, or undefined if cancelled. */
  chooseImage(): Promise<string | undefined>;
  chooseImages(): Promise<string[]>;
  /** Resolves a path to something an <img> can show, including photos not yet published. */
  imageSrc(path?: string): string | undefined;
  openForm(spec: FormSpec): Promise<FormResult>;
  /** Opens the style panel for one section. */
  openSectionStyle?(sectionId: string): void;
}

interface SiteContextValue {
  data: SiteData;
  edit?: EditApi;
}

const SiteContext = createContext<SiteContextValue>({ data: defaultContent });

export function SiteProvider({ data, edit, children }: { data: SiteData; edit?: EditApi; children: ReactNode }) {
  const value = useMemo(() => ({ data, edit }), [data, edit]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);

/** Maps a content image path to an <img> src (validated, or a local preview in the editor). */
export function useImageSrc() {
  const { edit } = useSite();
  return (path?: string) => (edit ? edit.imageSrc(path) : safeImagePath(path));
}
